import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin,
  Home,
  Building2,
  Maximize,
  Layers,
  Compass,
  Phone,
  Mail,
  MessageCircle,
  X,
  Maximize2
} from 'lucide-react';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const PublicPortfolioView = () => {
  const { token } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/public/portfolios/${token}`);
        if (response.data.success) {
          setPortfolio(response.data.data);
        } else {
          setError('Bu portföy bulunamadı veya paylaşımı kaldırılmış.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Bu portföy bulunamadı veya paylaşımı kaldırılmış.');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Bulunamadı</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const getRelationName = (rel) => rel?.name || "-";

  // Format price
  const formatPrice = (price) => {
    if (!price) return '-';
    return Number(price).toLocaleString('tr-TR') + ' ₺';
  };

  // Agent Contact String
  const agentPhone = portfolio.agent?.phone || '';
  const agentPhoneClean = agentPhone.replace(/\D/g, ''); // Remove non-digits for wa.me

  const allImages = [
    ...(portfolio.cover_photo ? [portfolio.cover_photo] : []),
    ...(Array.isArray(portfolio.images) ? portfolio.images : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Photo Gallery (Simple Slider) */}
      <div className="w-full h-64 md:h-[500px] bg-gray-900 relative">
        {allImages.length > 0 ? (
          <>
            <div 
              className="w-full h-full cursor-pointer relative group"
              onClick={() => {
                setLightboxIndex(activeImage);
                setLightboxOpen(true);
              }}
            >
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}/../storage/${allImages[activeImage]}`}
                alt={portfolio.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30">
                  <Maximize2 className="text-white" size={32} />
                </div>
              </div>
            </div>
            {allImages.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 px-4 overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded border-2 overflow-hidden flex-shrink-0 transition-all ${activeImage === idx ? 'border-primary-500 opacity-100' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={`${process.env.REACT_APP_API_BASE_URL}/../storage/${img}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
            <Home size={64} className="mb-4 opacity-50" />
            <p>Görsel bulunmamaktadır</p>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {portfolio.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={18} />
                      <span>{getRelationName(portfolio.city)}, {getRelationName(portfolio.district)}, {getRelationName(portfolio.neighborhood)}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-primary-50 px-6 py-4 rounded-xl text-center md:text-right flex-shrink-0 border border-primary-100">
                  <p className="text-sm font-semibold text-primary-600 mb-1 uppercase tracking-wider">{getRelationName(portfolio.listing_type)}</p>
                  <p className="text-2xl md:text-4xl font-extrabold text-gray-900">{formatPrice(portfolio.list_price)}</p>
                </div>
              </div>

              {/* Main Attributes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-gray-100">
                <div className="flex flex-col items-center md:items-start p-3">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Layers size={18} />
                    <span className="text-sm font-medium">Oda Sayısı</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{getRelationName(portfolio.room_definition)}</span>
                </div>
                <div className="flex flex-col items-center md:items-start p-3 border-l md:border-l-0 border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Maximize size={18} />
                    <span className="text-sm font-medium">Net M²</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{portfolio.net_sqm || '-'}</span>
                </div>
                <div className="flex flex-col items-center md:items-start p-3 border-t md:border-t-0 md:border-l border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Building2 size={18} />
                    <span className="text-sm font-medium">Kat</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{portfolio.floor || '-'}</span>
                </div>
                <div className="flex flex-col items-center md:items-start p-3 border-t md:border-t-0 border-l border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Compass size={18} />
                    <span className="text-sm font-medium">Cephe</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{getRelationName(portfolio.facade_type)}</span>
                </div>
              </div>

              {/* Additional Specs */}
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Özellikler</h3>
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm md:text-base">
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-gray-500">Emlak Tipi</span>
                    <span className="font-semibold text-gray-900">{getRelationName(portfolio.property_type)}</span>
                  </div>
                  {portfolio.residential_type && (
                    <div className="flex justify-between border-b border-gray-50 pb-2">
                      <span className="text-gray-500">Konut Tipi</span>
                      <span className="font-semibold text-gray-900">{getRelationName(portfolio.residential_type)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-gray-500">Bina Yaşı</span>
                    <span className="font-semibold text-gray-900">{portfolio.building_age || 'Sıfır'}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-gray-500">Brüt Metrekare</span>
                    <span className="font-semibold text-gray-900">{portfolio.gross_sqm ? portfolio.gross_sqm + ' m²' : '-'}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {portfolio.additional_info && (
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Açıklama</h3>
                  <div
                    className="prose max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed break-words [&_*]:break-all"
                    dangerouslySetInnerHTML={{ __html: portfolio.additional_info }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar / Agent Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex mx-auto items-center justify-center text-white text-3xl font-bold mb-4 shadow-md">
                  {portfolio.agent?.name?.charAt(0) || 'D'}
                </div>
                <h3 className="text-xl font-bold text-gray-900">{portfolio.agent?.name || 'Danışmanımız'}</h3>
                {portfolio.office && (
                  <p className="text-sm font-medium text-gray-500 mt-1">{portfolio.office.name}</p>
                )}
                <p className="text-xs text-primary-600 mt-1 font-semibold uppercase tracking-wider">Gayrimenkul Danışmanı</p>
              </div>

              <div className="space-y-3 mb-8">
                {portfolio.agent?.phone && (
                  <a href={`tel:${portfolio.agent.phone}`} className="flex items-center justify-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                    <Phone className="text-gray-600" size={20} />
                    <span className="text-gray-900 font-medium font-mono">{portfolio.agent.phone}</span>
                  </a>
                )}
                {portfolio.agent?.email && (
                  <a href={`mailto:${portfolio.agent.email}`} className="flex items-center justify-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                    <Mail className="text-gray-600" size={20} />
                    <span className="text-gray-900 font-medium">{portfolio.agent.email}</span>
                  </a>
                )}
              </div>

              <div className="space-y-3">
                {agentPhoneClean && (
                  <a
                    href={`https://wa.me/${agentPhoneClean}?text=Merhaba, ${portfolio.title} ilanı hakkında bilgi almak istiyorum.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-3 px-4 rounded-xl transition-colors shadow-sm shadow-[#25D366]/30"
                  >
                    <MessageCircle size={20} strokeWidth={2.5} />
                    WhatsApp'tan İletişime Geç
                  </a>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={allImages.map((img) => ({
          src: `${process.env.REACT_APP_API_BASE_URL}/../storage/${img}`,
        }))}
      />
    </div>
  );
};

export default PublicPortfolioView;
